export const SELLER_ATTRIBUTION_ALIASES = {};

export const GENERIC_SELLER_NAMES = new Set([
    'loja',
    'store',
    'varejo',
    'pdv',
    'pos',
    'caixa',
    'sistema',
    'system',
    'admin',
    'administrador'
]);

export const normalizeSellerKey = (value = '') => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export const normalizeSellerAliasKey = (value = '') => {
    const normalized = normalizeSellerKey(value);
    if (!normalized) return '';
    return normalizeSellerKey(SELLER_ATTRIBUTION_ALIASES[normalized] || normalized);
};

const tokenizeSellerValue = (value = '') => normalizeSellerAliasKey(value)
    .split(/[\s/._-]+/g)
    .map((token) => String(token || '').trim())
    .filter((token) => token.length >= 3 && !GENERIC_SELLER_NAMES.has(token));

const toSellerObject = (input) => {
    if (!input || typeof input !== 'object') {
        const name = String(input || '').trim();
        return name ? { name } : null;
    }
    return input;
};

export const buildSellerRegistry = (salesSellers, options = {}) => {
    const includeInactive = options?.includeInactive === true;
    const extraRecords = Array.isArray(options?.extraRecords) ? options.extraRecords : [];
    const records = [];
    const byExact = new Map();
    const byKey = new Map();
    const tokenCountMap = new Map();
    const tokenRecordMap = new Map();

    const attachAliases = (record, values) => {
        values
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .forEach((value) => {
                const normalized = normalizeSellerAliasKey(value);
                if (!normalized) return;
                byExact.set(normalized, record);
            });

        values
            .flatMap((value) => tokenizeSellerValue(value))
            .forEach((token) => {
                tokenCountMap.set(token, (tokenCountMap.get(token) || 0) + 1);
                if (!tokenRecordMap.has(token)) tokenRecordMap.set(token, record);
            });
    };

    const register = (rawInput) => {
        const input = toSellerObject(rawInput);
        if (!input) return;
        if (!includeInactive && input.active === false) return;

        const exactCandidates = [
            input?.key,
            input?.username,
            input?.id,
            input?.name
        ]
            .map((value) => normalizeSellerAliasKey(value))
            .filter(Boolean);

        const existing = exactCandidates
            .map((candidate) => byExact.get(candidate) || byKey.get(candidate))
            .find(Boolean) || null;

        const record = existing || {
            key: exactCandidates[0] || '',
            id: String(input?.id || '').trim(),
            username: String(input?.username || '').trim(),
            name: String(input?.name || input?.username || input?.id || '').trim(),
            commissionPct: Number(input?.commissionPct || 0) || 0,
            active: input?.active !== false
        };

        if (!record.key) {
            record.key = normalizeSellerAliasKey(record.username || record.id || record.name);
        }
        if (!record.key || GENERIC_SELLER_NAMES.has(record.key)) return;

        if (!record.name) {
            record.name = String(input?.name || input?.username || input?.id || record.key).trim();
        }
        if (!record.id && input?.id != null) {
            record.id = String(input.id).trim();
        }
        if (!record.username && input?.username != null) {
            record.username = String(input.username).trim();
        }
        if (input?.commissionPct != null) {
            record.commissionPct = Number(input.commissionPct || 0) || 0;
        }
        record.active = record.active !== false && input?.active !== false;

        attachAliases(record, [
            record.key,
            record.name,
            record.username,
            record.id,
            input?.key,
            input?.name,
            input?.username,
            input?.id
        ]);

        if (!existing) {
            records.push(record);
            byKey.set(record.key, record);
        }
    };

    (Array.isArray(salesSellers) ? salesSellers : []).forEach(register);
    extraRecords.forEach(register);

    const byToken = new Map();
    tokenCountMap.forEach((count, token) => {
        if (count === 1 && tokenRecordMap.has(token)) {
            byToken.set(token, tokenRecordMap.get(token));
        }
    });

    records.sort((a, b) => String(a?.name || a?.key || '').localeCompare(String(b?.name || b?.key || ''), 'pt-BR'));

    return { records, byExact, byToken, byKey };
};

export const resolveSellerRecord = (registry, candidates, options = {}) => {
    const values = (Array.isArray(candidates) ? candidates : [candidates])
        .map((value) => String(value || '').trim())
        .filter(Boolean);

    if (!values.length || !registry) return null;

    const exactMatch = values
        .map((value) => normalizeSellerAliasKey(value))
        .map((value) => registry.byExact.get(value) || registry.byKey.get(value))
        .find(Boolean);

    if (exactMatch) return exactMatch;
    if (options?.allowTokenMatch === false) return null;

    return values
        .flatMap((value) => tokenizeSellerValue(value))
        .map((token) => registry.byToken.get(token))
        .find(Boolean) || null;
};

export const findSellerConfigForUser = (systemUser, salesSellers) => {
    const registry = buildSellerRegistry(salesSellers);
    return resolveSellerRecord(registry, [systemUser?.username, systemUser?.name]);
};

export const buildSellerScope = ({ systemUser, salesSellers, override = null, extraValues = [] } = {}) => {
    const registry = buildSellerRegistry(salesSellers);
    const matchedRecord = override
        ? (
            resolveSellerRecord(registry, [override?.key, override?.name, override?.username, override?.id])
            || {
                key: normalizeSellerAliasKey(override?.key || override?.username || override?.id || override?.name),
                name: String(override?.name || override?.username || override?.id || override?.key || '').trim(),
                username: String(override?.username || '').trim(),
                id: String(override?.id || '').trim(),
                commissionPct: 0
            }
        )
        : resolveSellerRecord(registry, [systemUser?.username, systemUser?.name]);

    const keys = new Set();
    const add = (value) => {
        const normalized = normalizeSellerAliasKey(value);
        if (normalized) keys.add(normalized);
        const firstToken = tokenizeSellerValue(value)[0];
        if (firstToken) keys.add(firstToken);
    };

    [
        systemUser?.username,
        systemUser?.name,
        matchedRecord?.key,
        matchedRecord?.name,
        matchedRecord?.username,
        matchedRecord?.id,
        ...(Array.isArray(extraValues) ? extraValues : [])
    ].forEach(add);

    return { keys, matchedRecord, registry };
};

export const isSellerScopedUser = (systemUser, salesSellers, options = {}) => {
    if (!systemUser) return false;
    const excludedRoles = Array.isArray(options?.excludedRoles) ? options.excludedRoles : ['admin', 'supervisor'];
    const role = normalizeSellerKey(systemUser?.role || '');
    if (excludedRoles.includes(role)) return false;
    const { matchedRecord } = buildSellerScope({ systemUser, salesSellers });
    return !!matchedRecord?.key;
};
